import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import Utils from '../../shared/utils';
import { WorkspaceService } from '../workspace.service';
import { CommonService } from 'app/core/services/common.service';
import { FavouritesService } from 'app/favourites/services/favourites.service';
import { AppStore } from '@iv/store/app.store';
import * as Redux from 'redux';
import { AppState } from '@iv/store/app.reducer';
import * as FavoritesReducer from '../../favourites/redux/favorites.reducer';
import * as FuturesMonitorReducer from '../../futures-monitor/redux/futures-monitor.reducer';
import { getColumnsEntities } from '@iv/store/columns/columns.selectors';
import * as ColumnsActions from '../../store/columns/columns.actions';
import * as TabsActions from '../../store/tabs/tabs.actions';
import { getCurrentTab } from '@iv/store/tabs/tabs.selectors';
import { SummaryLineTableComponent } from '../summary-line-table/summary-line-table.component';
import { FuturesMonitorService } from 'app/futures-monitor/services/futures-monitor.service';
import { FormControl } from '@angular/forms';
import { interval, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Destroyer } from 'app/common/utils';
import { getPermissions } from '@iv/store/permissions/permissions.reducer';
import { PermissionTypes } from '@iv/store/permissions/permissions.models';
import { getTime } from '@iv/shared/helpers/common.helper';
import { Future, StockTypeEnum } from '@iv/model/instrument';
import { TabTypeEnum } from '@iv/model/tabs.model';

import * as moment from 'moment-timezone';
import {Group} from '@iv/model/group';

@Component({
  selector: 'iv-live-summary-line',
  templateUrl: './summary-line.component.html',
  styleUrls: ['./summary-line.component.scss']
})
export class SummaryLineComponent extends Destroyer implements OnInit, OnDestroy {

  constructor(
    private _favoritesService: FavouritesService,
    private _futuresMonitorService: FuturesMonitorService,
    private _workspaceService: WorkspaceService,
    private _commonService: CommonService,
    @Inject(AppStore) private store: Redux.Store<AppState>
  ) {
    super();
    this.unsubscribeStore = store.subscribe(() => this._updateState());
    this._updateState();
    this._workspaceService.getRefreshButtonClicked()
      .subscribe(() => {
        this.loadData();
      });
  }

  get ivx() {
    return this._ivx;
  }

  @Input() set ivx(value: number) {
    this._ivx = value;
  }

  get hv() {
    return this._hv;
  }

  @Input() set hv(value: number) {
    this._hv = value;
  }

  @Input() hideCharts;
  @Input() instrument: Future;
  @Input() isClose: boolean;
  @Input() hasOptions;
  @Output() ivxChange = new EventEmitter<any>();
  @Output() hvChange = new EventEmitter<any>();
  @Output() IVXInit = new EventEmitter<any>();
  @Output() HVInit = new EventEmitter<any>();
  @Output() stockInfoChanged = new EventEmitter<any>();
  @Output() isCloseChange = new EventEmitter<boolean>();

  @ViewChild(SummaryLineTableComponent, {static: false})
  public summaryLineTable !: SummaryLineTableComponent;
  public currentTab = getCurrentTab(this.store.getState());

  groups: Group[] = [];
  instrumentInfo = null;
  closeDate = new Date();
  updateDate = null;
  settingColumns: any[] = [];
  columns: any = {};
  editColumnsPopupHeight = 370;
  isMac = !!navigator.platform.match(/(Mac|iPhone|iPod|iPad)/i);
  isMobile = Utils.isMobile();
  editColumnsClass = (this.isMac
    ? 'edit-columns-popup option-monitor-columns-popup mac'
    : 'edit-columns-popup option-monitor-columns-popup') + (this.isMobile ? ' mobile' : '');
  editColumnsPopupVisible = false;
  editColumnsPopupPosition: any = {my: 'left top', at: 'bottom', of: '#editSummaryLineColumnsButton'};
  weekday = ['Sun', 'Mon', 'Tue', 'Wed', 'Th', 'Fri', 'Sat'];
  month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'June', 'July', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
  mode = getPermissions(this.store.getState()).find(permission => {
    if (this.currentTab.type === TabTypeEnum.OPTION_CHAIN) {
      return permission === PermissionTypes.DELAYED_STOCKS || permission === PermissionTypes.RT_STOCKS;
    } else {
      return permission === PermissionTypes.EOD_FUTURES || permission === PermissionTypes.RT_FUTURES;
    }
  });
  isDelayed = this.currentTab.type === TabTypeEnum.OPTION_CHAIN ? this.mode === PermissionTypes.DELAYED_STOCKS : this.mode === PermissionTypes.EOD_FUTURES;
  isRt = this.currentTab.type === TabTypeEnum.OPTION_CHAIN ? this.mode === PermissionTypes.RT_STOCKS : this.mode === PermissionTypes.RT_FUTURES;
  updateDateString: string;
  closeDateString: string;
  defaultColumns: any;
  visibleColumns: any;
  volatilityTablePopupVisible = false;
  showIvx = true;
  isEodDate;
  currentNYDate: Date;
  stockInfo: object;

  public expiration = new FormControl();
  public id = this.currentTab.type === TabTypeEnum.OPTION_CHAIN ? this.currentTab.data.instrument.stockId : this.currentTab.data.future.underlyingId;
  public expirations: string[];
  public dynamicSymbol: string;

  private _timezone: number;
  private unsubscribeStore;
  private _ivx;
  private _hv;

  private static getCurrentNYDate(): Date {
    const todayDate: string = new Date().toLocaleString("en-US", {timeZone: "America/New_York"});
    return new Date(todayDate);
  }

  public ngOnInit(): void {
    this.mode.toLowerCase();

    this.getExpirations();
    this.getColumns();

    interval(20000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.loadData());

    this.showIvx = !!(this.hasOptions);

    if (this.currentTab.type === TabTypeEnum.FUTURES_OPTION_CHAIN) {
      this.expiration.valueChanges
        .subscribe((expiration: string) => {
          this.store.dispatch(TabsActions.updateServiceTabData({
            name: 'futExpDate',
            value: expiration
          }));
          this.loadData();
        });
    } else {
      this.loadData();
    }
  }

  public ngOnDestroy(): void {
    if (typeof this.unsubscribeStore === 'function') {
      this.unsubscribeStore();
    }

    super.ngOnDestroy();
  }

  private getCurrentApi(tabType: string): Observable<any> {
    switch (tabType) {
      case 'instrument':
        return this._workspaceService.getInstrumentInfo(this.getParamsToInstrumentInfo(tabType));
      case 'future':
        return this._workspaceService.getFutureInfo(this.getParamsToInstrumentInfo(tabType));
    }
  }

  private loadData(): void {
    this.getCurrentApi(this.currentTab.type)
      .subscribe(data => {
        if (data.body) {
          data = data.body;
        }

        const roundToHundredths: string[] = ['highPrice52Wk', 'lowPrice52Wk', 'price', 'yield'];
        const roundToInteger: string[] = ['marketCap'];

        for (const filed in data) {
          if (roundToHundredths.includes(filed)) {
            data[filed] = +data[filed]?.toFixed(2);
          }

          if (roundToInteger.includes(filed)) {
            data[filed] = +data[filed]?.toFixed(0);
          }
        }

        this.stockInfo = data;
        this.stockInfoChanged.emit(data);
        if (!this.instrumentInfo) {
          this.store.dispatch(TabsActions.updateServiceTabData({
            name: 'center',
            value: data.price
          }));
        }
        this.currentNYDate = SummaryLineComponent.getCurrentNYDate();
        this.updateDate = (data.updateDate) ? new Date(Date.parse(data.updateDate)) : null;
        let myMoment: moment.Moment = moment(data.atClose).tz('America/New_York');
        this.closeDate = myMoment.toDate();

        this.updateDateString = (data.updateDate !== null) ? getTime(data.updateDate) : null;
        this.closeDateString = getTime(data.atClose);
        if (this.closeDateString === '00:00 AM EST') {
          this.closeDateString = '4:00 PM EST';
        }
        this.isClose = this.isMarketClose();
        this.isEodDate = this.isEodDateUsed();
        this.isCloseChange.emit(this.isClose && this._isMarketCloseBeforeSession());
        this.instrumentInfo = data;
        if (this.currentTab.type === TabTypeEnum.FUTURES_OPTION_CHAIN) {
          this.dynamicSymbol = data.symbol;
        }
        this._updateHeaderTime();
      });
  }

  private getExpirations(): void {
    if (this.currentTab.type === TabTypeEnum.FUTURES_OPTION_CHAIN) {
      this._workspaceService.getFutureExpirations(this.id)
        .subscribe((expirations) => {
          this.expirations = expirations.body;
          if (this.currentTab.data.serviceTabs[0].filters.futExpDate) {
            this.setFutExpirationDate(this.currentTab.data.serviceTabs[0].filters.futExpDate);
          } else {
            this.setFutExpirationDate(this.expirations[0]);
          }
        });
    }
  }

  private setFutExpirationDate(expirationDate): void {
    this.expiration.setValue(expirationDate);
    this.store.dispatch(TabsActions.updateServiceTabData({
      name: 'futExpDate',
      value: expirationDate
    }));
  }

  private getColumns(): void {
    this._workspaceService
      .getColumns(this.hasOptions, this.instrument.regionId, this.currentTab.type)
      .subscribe(data => {
        if (!this.defaultColumns) {
          this._updateDefaultConfig(data.visibleColumns.map(x => x));
        }

        if (!this.visibleColumns && this.hasOptions) {
          this.store.dispatch(TabsActions.updateTabData({
            name: 'summaryLineVisibleColumns',
            value: this.defaultColumns.filter(column => {
              return !(
                (this.instrument.stockType === StockTypeEnum.INDEX || this.instrument.stockType === StockTypeEnum.FX_INDEX)
                && (column === 'eps' || column === 'pe' || column === 'marketCap')
              );
            })
          }));
        }

        if (this.instrument.regionId !== 1) {
          const visibleColumns = data.visibleColumns.map(x => x).filter(column => {
            return !(
              (this.instrument.stockType === StockTypeEnum.INDEX || this.instrument.stockType === StockTypeEnum.FX_INDEX)
              && (column === 'eps' || column === 'pe' || column === 'marketCap')
            );
          });
          visibleColumns.push('dividend');
          if ((this.instrument.stockType === StockTypeEnum.STOCK || this.instrument.stockType === StockTypeEnum.FUND)) {
            visibleColumns.push('ivp');
            visibleColumns.push('beta180D');
          }
          this.store.dispatch(TabsActions.updateTabData({
            name: 'summaryLineVisibleColumns',
            value: visibleColumns
          }));
        }

        if (this.hasOptions && (this.instrument.stockType === StockTypeEnum.INDEX || this.instrument.stockType === StockTypeEnum.FX_INDEX)) {
          delete data.columns['pe'];
          delete data.columns['eps'];
          delete data.columns['marketCap'];

          delete data.settingColumns['pe'];
          delete data.settingColumns['eps'];
          delete data.settingColumns['marketCap'];
        }

        if (!this.hasOptions) {
          this.store.dispatch(TabsActions.updateTabData({
            name: 'summaryLineVisibleColumns',
            value: Object.keys(data.columns)
          }));
        }

        if (this.currentTab.type !== 'future') {
          data.columns.price.caption = 'Implied Price';
          data.columns.price.tip = 'Implied spot based on put-call parity';
        }

        this.columns = Object.assign({}, data.columns);
        this.settingColumns = Object.values(data.settingColumns).map(x => Object.assign({}, x));
      });
  }

  private getParamsToInstrumentInfo(tabType: string): Object {
    return {
      [this.currentTab.type === TabTypeEnum.OPTION_CHAIN ? 'region' : 'regionId']: this.currentTab.data[this.currentTab.type].regionId,
      [tabType === 'instrument' ? 'stockId' : 'underlyingId']: this.id,
      [tabType === 'instrument' ? 'rtMode' : 'mode']: this.mode.split('_')[0],
      hasOptions: this.hasOptions.toString(),
      expirationDate: this.expiration.value
    };
  }

  isEodDateUsed() {
    return this.updateDateString == null;
  }

  isMarketClose(): boolean {
    return this.isWeekend()
      || this.currentNYDate.getTime() < this.getMarketOpenDate().getTime()
      || this.currentNYDate.getTime() > this.getMarketCloseDate().getTime();
  }

  getCurrentTimeInUtc() {
    const now = new Date();
    now.setHours(now.getHours() + (now.getTimezoneOffset() / 60));
    return now;
  }

  isWeekend() {
    const day = new Date().getDay();
    return day === 0 || day === 6;
  }

  getMarketOpenDate(): Date {
    return this.getDateWithHours(9, 30 + (this.isDelayed ? 20 : 0));
  }

  getMarketCloseDate(): Date {
    return this.getDateWithHours(16, 15 + (this.isDelayed ? 20 : 0));
  }

  getDateWithHours(hour, minute) {
    const date = new Date();
    date.setHours(hour);
    date.setMinutes(minute);
    date.setSeconds(0);
    date.setMilliseconds(0);
    return date;
  }

  selectGroup(group: any) {
    this._addInstrumentToGroup(group.id, this.currentTab.type === TabTypeEnum.OPTION_CHAIN ? this.instrument.stockId : this.instrument.underlyingId);
  }

  onEditColumnsClick() {
    if (this.isMobile) {
      this.editColumnsPopupPosition = {my: 'center', at: 'center', of: window};
    } else {
      this.editColumnsPopupPosition = {my: 'left top', at: 'bottom', of: '#editSummaryLineColumnsButton'};
    }
    this.editColumnsPopupVisible = true;
  }

  onColumnSwitch(columns: string[]) {
    this.editColumnsPopupVisible = false;
    this.store.dispatch(TabsActions.updateTabData({
      name: 'summaryLineVisibleColumns',
      value: columns
    }));
    this.loadData();
  }

  saveDefault(columns: string[]) {
    this._updateDefaultConfig(columns.map(x => x));
  }

  setIvx($event: any) {
    this.ivxChange.emit($event);
  }

  setHv($event: any) {
    this.hvChange.emit($event);
  }

  onVolatilityTableBtnClick() {
    this.loadData();
    this.volatilityTablePopupVisible = true;
  }

  public getInstrumentName(instrument: Future): string {
    if (this.currentTab.type === TabTypeEnum.OPTION_CHAIN) {
      return `${instrument.symbol}${instrument.exchange ? ':' + instrument.exchange : ''}`;
    }

    return `${this.dynamicSymbol ? this.dynamicSymbol : instrument.symbol}:${instrument.code}`;
  }

  private _updateDefaultConfig(columns: string[]) {
    this.store.dispatch(ColumnsActions.updateColumnsConfig({
      name: this.currentTab.type === TabTypeEnum.OPTION_CHAIN ? 'SummaryLine' : 'FuturesSummaryLine',
      value: columns
    }));
  }

  private _updateState() {
    const state = this.store.getState();
    const columnsConfig = getColumnsEntities(state)[this.currentTab.type === TabTypeEnum.OPTION_CHAIN ? 'SummaryLine' : 'FuturesSummaryLine'];
    if (columnsConfig) {
      this.defaultColumns = columnsConfig;
    }

    if (this.currentTab && this.currentTab.data.summaryLineVisibleColumns) {
      this.visibleColumns = this.currentTab.data.summaryLineVisibleColumns.map(x => x);
    }
    this.groups = this.currentTab.type === TabTypeEnum.OPTION_CHAIN ? FavoritesReducer.getGroups(state) : FuturesMonitorReducer.getGroups(state);
  }

  private compareDate(date: Date) {
    if (date.getDate() === this.currentNYDate.getDate() && date.getMonth() === this.currentNYDate.getMonth() && date.getFullYear() === this.currentNYDate.getFullYear()) {
      return (this.isRt ? 'real-time mode' : '20-min delayed mode');
    } else {
      return 'end-of-day';
    }
  }

  private _updateHeaderTime() {
    try {
      const date: Date = this.updateDate ? this.updateDate : this.closeDate;
      const momentDate: moment.Moment = date === this.updateDate ?  moment(date).tz('America/New_York') : moment(date);
      let mode = "";
      if (this.currentTab.type === TabTypeEnum.OPTION_CHAIN) {
        mode = this.isClose ? 'market closed' : this.compareDate(date);
      } else {
        mode = 'end-of-day';
      }
      const timeContent = `${this._getDayOfWeek(momentDate.day())}, ${this._getMonth(momentDate.month())} ${momentDate.date()}, ${momentDate.year()};
      <br/>${mode}`;
      console.log(`time = ${timeContent}`)
      window.parent.postMessage({message: 'timeHeaderContent', value: timeContent}, '*');
    } catch (e) {
    }
  }

  private _getDayOfWeek(index) {
    return this.weekday[index];
  }

  private _getMonth(index) {
    return this.month[index];
  }

  private _addInstrumentToGroup(groupId, id) {
    const maxGroupInstruments = 200;
    const service = this.currentTab.type === TabTypeEnum.OPTION_CHAIN
      ? '_favoritesService'
      : '_futuresMonitorService';

    this[service].getGroupData(this.mode, groupId).subscribe(group => {
      if (group.body.length === maxGroupInstruments) {
        this.notify(`Up to ${maxGroupInstruments} tickers is allowed.
        Please delete one or more tickers to add a new one.`, 'error');
      }
      else {
        this[service].addInstruments(groupId, id).subscribe(p => {
          if (p.RETURN_VALUE === 0 || p.RETURN_VALUE === 1 || p.RETURN_VALUE === 2) {
            this.notify('Instrument added successfully.', 'success');
          }
        });
      }
    });
  }

  private notify(text, type) {
    this._commonService.sendNotification(text, type);
  }

  private _isMarketCloseBeforeSession(): boolean {
    return (this.getCurrentTimeInUtc().getTime() < this.getMarketOpenDate().getTime());
  }
}
